import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  ArrowLeft, User, Phone, Mail, MapPin, Calendar, 
  CreditCard, FileText, Shield, Bell, Home, DollarSign,
  CheckCircle, XCircle, Clock, Download, Printer, Edit,
  Send, IndianRupee, Receipt
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import DepositCard from './DepositCard';
import { PutOnNoticeModal, EditCheckoutDateModal, MarkExitedModal, CancelNoticeModal } from './TenantLifecycleModals';
import RecordPaymentModal from './RecordPaymentModal';
import DocumentsList from './DocumentsList';
import { EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { LastSyncedBadge } from './LastSyncedBadge';
import { track } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format a day number as English ordinal: 1 -> 1st, 22 -> 22nd, etc.
const ordinal = (n) => {
  if (!n) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const TenantProfile = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [editCheckoutOpen, setEditCheckoutOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [cancelNoticeOpen, setCancelNoticeOpen] = useState(false);
  const [recordPayOpen, setRecordPayOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchTenantProfile = async () => {
    try {
      const response = await axios.get(`${API}/tenants/${tenantId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setLastSyncedAt(new Date());
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch tenant profile",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Hot refresh on any tenant or payment change anywhere in the app
  useDataSync([EVT.TENANT, EVT.PAYMENT], () => fetchTenantProfile());
  useRefetchOnFocus(fetchTenantProfile);


  const getIcon = (iconName) => {
    const icons = {
      'user-plus': User,
      'home': Home,
      'file-text': FileText,
      'shield': Shield,
      'credit-card': CreditCard,
      'dollar-sign': DollarSign,
      'bell': Bell
    };
    const IconComponent = icons[iconName] || User;
    return <IconComponent size={20} />;
  };

  const handleSendReminder = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/reminders/send`,
        { tenant_ids: [tenantId], channel: 'email' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Reminder Sent",
        description: "Rent reminder has been sent successfully"
      });
      fetchTenantProfile(); // Refresh to show new reminder in timeline
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send reminder",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintProfile = () => {
    window.print();
  };

  const handleDownloadReport = () => {
    const { tenant, stats, timeline } = profile;
    
    // Create a simple text report
    let report = `TENANT PROFILE REPORT\n`;
    report += `======================\n\n`;
    report += `Name: ${tenant.name}\n`;
    report += `Phone: ${tenant.phone}\n`;
    report += `Email: ${tenant.email || 'N/A'}\n`;
    report += `Status: ${tenant.status}\n`;
    report += `Property: ${profile.property?.name} - Room ${profile.room?.room_number}\n`;
    report += `Move-in Date: ${formatDate(tenant.move_in_date)}\n\n`;
    
    report += `FINANCIAL SUMMARY\n`;
    report += `=================\n`;
    report += `Monthly Rent: ₹${tenant.rent_amount}\n`;
    report += `Security Deposit: ₹${tenant.deposit_amount}\n`;
    report += `Total Paid: ₹${stats.total_paid}\n`;
    report += `Total Pending: ₹${stats.total_pending}\n`;
    report += `Total Payments: ${stats.total_payments}\n`;
    report += `Agreements: ${stats.agreements_count}\n\n`;
    
    report += `ACTIVITY TIMELINE\n`;
    report += `=================\n`;
    timeline.forEach(event => {
      report += `${formatDateTime(event.date)} - ${event.title}\n`;
      report += `  ${event.description}\n\n`;
    });
    
    // Download as text file
    const blob = new Blob([report], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenant_report_${tenant.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Report Downloaded",
      description: "Tenant report has been downloaded successfully"
    });
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      red: 'bg-red-100 text-red-600',
      orange: 'bg-orange-100 text-orange-600',
      yellow: 'bg-yellow-100 text-yellow-600'
    };
    return colors[color] || colors.blue;
  };

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  function formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const filteredTimeline = profile?.timeline.filter(event => {
    if (filterType === 'all') return true;
    return event.type === filterType;
  }) || [];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tenant profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Tenant not found</p>
        <Button onClick={() => navigate('/manager/people/tenants')} className="mt-4">
          Back to Tenants
        </Button>
      </div>
    );
  }

  const { tenant, property, room, stats } = profile;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/manager/people/tenants')}
            variant="outline"
            size="sm"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Tenant Profile</h1>
        </div>
        
        {/* Quick Actions */}
        <div className="flex gap-2 items-center">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchTenantProfile} refreshing={loading} />
          <Button
            onClick={handleSendReminder}
            disabled={actionLoading}
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            <Send size={16} className="mr-2" />
            Send Reminder
          </Button>
          <Button
            onClick={() => { track('cta_record_payment_clicked', { surface: 'tenant_profile', tenant_id: tenantId }); setRecordPayOpen(true); }}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            data-testid="tenant-record-payment-btn"
          >
            <IndianRupee size={16} className="mr-2" />
            Record Payment
          </Button>
          <Button
            onClick={handlePrintProfile}
            size="sm"
            variant="outline"
          >
            <Printer size={16} className="mr-2" />
            Print
          </Button>
          <Button
            onClick={handleDownloadReport}
            size="sm"
            variant="outline"
          >
            <Download size={16} className="mr-2" />
            Download Report
          </Button>
        </div>
      </div>

      {/* Profile Header Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white text-3xl font-bold">
            {tenant.name.charAt(0)}
          </div>
          
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{tenant.name}</h2>
                <div className="flex gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                    tenant.status === 'notice' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {tenant.status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    tenant.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {tenant.payment_status === 'paid' ? 'Rent Paid' : 'Rent Due'}
                  </span>
                  {tenant.kyc_status && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      tenant.kyc_status === 'verified' ? 'bg-purple-100 text-purple-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      KYC: {tenant.kyc_status}
                    </span>
                  )}
                  {user?.role === 'super_admin' && (
                    <button
                      onClick={async () => {
                        const next = !tenant.is_test;
                        try {
                          const res = await axios.patch(
                            `${API}/tenants/${tenant.id}`,
                            { is_test: next },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setProfile(p => ({ ...p, tenant: { ...p.tenant, is_test: res.data.is_test } }));
                          toast({
                            title: next ? 'Marked as test tenant' : 'Unmarked as test',
                            description: next
                              ? 'Hidden from home-page counts by default.'
                              : 'Now included in home-page counts.',
                          });
                        } catch (err) {
                          toast({
                            title: 'Could not update test flag',
                            description: err.response?.data?.detail || '',
                            variant: 'destructive',
                          });
                        }
                      }}
                      data-testid="tenant-test-toggle"
                      title={tenant.is_test ? 'Click to unmark' : 'Click to mark as test — hidden from dashboards'}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        tenant.is_test
                          ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {tenant.is_test ? '🧪 TEST' : '+ Mark test'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={18} />
                <span className="text-sm">{tenant.phone}</span>
              </div>
              {tenant.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail size={18} />
                  <span className="text-sm">{tenant.email}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={18} />
                <span className="text-sm">{property?.name} - Room {room?.room_number}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar size={18} />
                <span className="text-sm">Joined: {formatDate(tenant.move_in_date)}</span>
              </div>
            </div>

            {/* Check-in / Checkout dates + lifecycle actions */}
            <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Check-in date</p>
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 mt-1">
                  <Calendar size={14} className="text-green-600" />
                  {tenant.check_in_date ? new Date(tenant.check_in_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
                  Checkout date
                  {tenant.status === 'notice' && <span className="text-[9px] bg-orange-100 text-orange-800 px-1 py-0.5 rounded font-bold">PLANNED</span>}
                  {tenant.status === 'exited' && <span className="text-[9px] bg-gray-200 text-gray-700 px-1 py-0.5 rounded font-bold">ACTUAL</span>}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                    <Calendar size={14} className={tenant.status === 'exited' ? 'text-gray-500' : 'text-orange-600'} />
                    {tenant.check_out_date
                      ? new Date(tenant.check_out_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                      : <span className="text-gray-400 italic">Not set</span>}
                  </p>
                  <button
                    onClick={() => setEditCheckoutOpen(true)}
                    className="text-[10px] text-blue-600 hover:bg-blue-50 rounded px-1.5 py-0.5 font-semibold inline-flex items-center gap-0.5"
                    data-testid="edit-checkout-btn"
                    title="Edit checkout date"
                  >
                    <Edit size={10} /> Edit
                  </button>
                </div>
              </div>
              <div className="flex md:justify-end items-start">
                {tenant.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setNoticeOpen(true)}
                    className="border-orange-200 text-orange-700 hover:bg-orange-50"
                    data-testid="put-on-notice-btn"
                  >
                    <Clock size={14} className="mr-1" /> Put on notice
                  </Button>
                )}
                {tenant.status === 'notice' && (
                  <div className="flex flex-col gap-1.5 items-end">
                    <Button
                      size="sm"
                      onClick={() => setExitOpen(true)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="mark-exited-btn"
                    >
                      <XCircle size={14} className="mr-1" /> Mark exited
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCancelNoticeOpen(true)}
                      className="border-green-300 text-green-700 hover:bg-green-50 h-7 text-[11px] px-2"
                      data-testid="cancel-notice-btn"
                    >
                      <CheckCircle size={11} className="mr-1" /> Cancel notice (tenant stays)
                    </Button>
                  </div>
                )}
                {tenant.status === 'exited' && (
                  <span className="text-xs text-gray-500 italic">Exited — room freed</span>
                )}
              </div>
            </div>

            {/* Rent Details */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
              <div>
                <p className="text-sm text-gray-600">Monthly Rent</p>
                <p className="text-2xl font-bold text-gray-900">₹{tenant.rent_amount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Rent Due Date</p>
                <div className="flex items-center gap-2 mt-1" data-testid="rent-due-day-edit">
                  <select
                    value={tenant.rent_due_day || ''}
                    onChange={async (e) => {
                      const day = parseInt(e.target.value, 10);
                      if (!day || day < 1 || day > 31) return;
                      try {
                        const res = await axios.patch(
                          `${API}/tenants/${tenant.id}`,
                          { rent_due_day: day },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setProfile(p => ({ ...p, tenant: { ...p.tenant, rent_due_day: res.data.rent_due_day } }));
                        toast({ title: 'Rent due day updated', description: `Rent is now due on the ${ordinal(day)} of every month.` });
                      } catch (err) {
                        toast({
                          title: 'Update failed',
                          description: err.response?.data?.detail || 'Could not save due day',
                          variant: 'destructive',
                        });
                      }
                    }}
                    className="text-2xl font-bold text-orange-600 bg-transparent border-b-2 border-orange-200 hover:border-orange-400 focus:border-orange-600 focus:outline-none px-1 pr-5 cursor-pointer"
                    data-testid="rent-due-day-select"
                    title="Edit rent due day"
                  >
                    {!tenant.rent_due_day && <option value="">—</option>}
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{ordinal(d)}</option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-500">of month</span>
                </div>
              </div>
            </div>

            {/* Security Deposit — full card with progress + partial-payment support */}
            <div className="mt-4">
              <DepositCard
                tenantId={tenant.id}
                token={token}
                onChanged={fetchTenantProfile}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Additional Details Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* KYC Details Card */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield size={20} className="text-purple-600" />
            KYC Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                tenant.kyc_status === 'verified' ? 'bg-green-100 text-green-800' :
                tenant.kyc_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {tenant.kyc_status || 'Not Submitted'}
              </span>
            </div>
            {tenant.kyc_type && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Document Type:</span>
                <span className="text-sm font-medium">{tenant.kyc_type}</span>
              </div>
            )}
            {tenant.kyc_number && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Document Number:</span>
                <span className="text-sm font-medium">{tenant.kyc_number}</span>
              </div>
            )}
            {tenant.aadhar_number && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Aadhar:</span>
                <span className="text-sm font-medium">
                  {tenant.aadhar_number.replace(/(\d{4})(\d{4})(\d{4})/, '****-****-$3')}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Emergency Contact Card */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Phone size={20} className="text-red-600" />
            Emergency Contact
          </h3>
          <div className="space-y-3">
            {tenant.emergency_contact_name ? (
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="text-sm font-medium">{tenant.emergency_contact_name}</span>
                </div>
                {tenant.emergency_contact_phone && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Phone:</span>
                    <span className="text-sm font-medium">{tenant.emergency_contact_phone}</span>
                  </div>
                )}
                {tenant.emergency_contact_relation && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Relation:</span>
                    <span className="text-sm font-medium">{tenant.emergency_contact_relation}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">No emergency contact added</p>
            )}
          </div>
        </Card>
      </div>

      {/* Documents Vault — KYC, agreements, supporting docs for this tenant */}
      <Card className="p-6">
        <DocumentsList
          scope="tenant"
          tenantId={tenant.id}
          tenantName={tenant.name}
        />
      </Card>

      {/* Current Month Balance Card - Prominent Display */}
      <Card className={`p-6 ${
        stats.balance_status === 'due' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-300' :
        stats.balance_status === 'advance' ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-300' :
        'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${
              stats.balance_status === 'due' ? 'bg-red-100' :
              stats.balance_status === 'advance' ? 'bg-blue-100' :
              'bg-green-100'
            }`}>
              <Receipt className={
                stats.balance_status === 'due' ? 'text-red-600' :
                stats.balance_status === 'advance' ? 'text-blue-600' :
                'text-green-600'
              } size={32} />
            </div>
            <div>
              <p className="text-sm text-gray-600">
                {stats.balance_status === 'due' ? 'Current Month Balance Due' :
                 stats.balance_status === 'advance' ? 'Advance Payment' :
                 'Current Month'}
              </p>
              <p className={`text-4xl font-bold ${
                stats.balance_status === 'due' ? 'text-red-600' :
                stats.balance_status === 'advance' ? 'text-blue-600' :
                'text-green-600'
              }`}>
                ₹{stats.current_balance || 0}
              </p>
              <div className="mt-2 text-sm text-gray-600">
                <span>Expected: ₹{stats.current_month_expected} | </span>
                <span>Paid: ₹{stats.current_month_paid}</span>
              </div>
            </div>
          </div>
          {stats.balance_status === 'due' && (
            <Button
              onClick={() => setRecordPayOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="tenant-collect-balance-btn"
            >
              <IndianRupee size={16} className="mr-2" />
              Collect Balance
            </Button>
          )}
          {stats.balance_status === 'paid' && (
            <div className="text-green-600 font-semibold flex items-center gap-2">
              <CheckCircle size={24} />
              <span>Fully Paid</span>
            </div>
          )}
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Paid (All Time)</p>
              <p className="text-2xl font-bold text-green-600">₹{stats.total_paid}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Bills</p>
              <p className="text-2xl font-bold text-orange-600">₹{stats.total_pending}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Clock className="text-orange-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Payments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_payments}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <CreditCard className="text-blue-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Agreements</p>
              <p className="text-2xl font-bold text-gray-900">{stats.agreements_count}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FileText className="text-purple-600" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Dense Info Grid: Ledger + KYC + Complaints */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tenant-profile-info-grid">
        {/* KYC / Identity Card */}
        <Card className="p-5" data-testid="profile-kyc-card">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="text-purple-600" size={18} />
            <h3 className="font-semibold text-gray-900">Identity & KYC</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">KYC Status</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                tenant.kyc_status === 'verified' ? 'bg-green-100 text-green-800' :
                tenant.kyc_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                tenant.kyc_status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
              }`}>{tenant.kyc_status || 'none'}</span>
            </div>
            {tenant.kyc_type && (
              <div className="flex justify-between">
                <span className="text-gray-500">Document Type</span>
                <span className="font-medium capitalize">{tenant.kyc_type.replace('_', ' ')}</span>
              </div>
            )}
            {tenant.kyc_number && (
              <div className="flex justify-between">
                <span className="text-gray-500">Document #</span>
                <span className="font-mono text-xs">{tenant.kyc_number}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Phone</span>
              <span className="font-medium">{tenant.phone}</span>
            </div>
            {tenant.email && (
              <div className="flex justify-between">
                <span className="text-gray-500">Email</span>
                <span className="font-medium truncate max-w-[150px]">{tenant.email}</span>
              </div>
            )}
            {tenant.emergency_contact && (
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-500">Emergency</span>
                <span className="font-medium">{tenant.emergency_contact}</span>
              </div>
            )}
            {tenant.emergency_phone && (
              <div className="flex justify-between">
                <span className="text-gray-500">Emergency Phone</span>
                <span className="font-medium">{tenant.emergency_phone}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Financial Ledger */}
        <Card className="p-5" data-testid="profile-ledger-card">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="text-green-600" size={18} />
            <h3 className="font-semibold text-gray-900">Financial Ledger</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Monthly Rent</span>
              <span className="font-bold text-gray-900">₹{(tenant.rent_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Security Deposit</span>
              <span className="font-bold text-gray-900">₹{(tenant.deposit_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-gray-500">Total Paid</span>
              <span className="font-bold text-green-700">₹{(stats.total_paid || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Outstanding</span>
              <span className={`font-bold ${stats.total_pending > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                ₹{(stats.total_pending || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-gray-500">Reminders Sent</span>
              <span className="font-medium">{stats.reminders_sent || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Agreements</span>
              <span className="font-medium">{stats.agreements_count || 0}</span>
            </div>
          </div>
        </Card>

        {/* Complaints */}
        <Card className="p-5" data-testid="profile-complaints-card">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="text-red-600" size={18} />
            <h3 className="font-semibold text-gray-900">Complaints</h3>
            <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
              {stats.open_complaints || 0} open
            </span>
          </div>
          {!profile.complaints || profile.complaints.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-4 text-center">No complaints filed</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {profile.complaints.slice(0, 5).map(c => (
                <div key={c.id} className="p-2 border border-gray-100 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-900 truncate flex-1">{c.title}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      c.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      c.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{c.priority}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                    <span>{c.category}</span>
                    <span>•</span>
                    <span className={c.status === 'resolved' ? 'text-green-600' : c.status === 'open' ? 'text-red-600' : 'text-yellow-600'}>
                      {c.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
              {profile.complaints.length > 5 && (
                <p className="text-[10px] text-gray-400 text-center pt-1">+{profile.complaints.length - 5} more</p>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Financial Summary Banner - Only for older pending bills */}
      {stats.total_pending > 0 && (
        <Card className="p-6 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-red-100 rounded-full">
                <Receipt className="text-red-600" size={32} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Outstanding Balance</p>
                <p className="text-3xl font-bold text-red-600">₹{stats.total_pending}</p>
                <p className="text-xs text-gray-600 mt-1">Please collect payment from tenant</p>
              </div>
            </div>
            <Button
              onClick={() => setRecordPayOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="tenant-record-payment-now-btn"
            >
              <IndianRupee size={16} className="mr-2" />
              Record Payment Now
            </Button>
          </div>
        </Card>
      )}

      {/* Timeline Filters */}
      <Card className="p-4">
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setFilterType('all')}
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
          >
            All ({profile.timeline.length})
          </Button>
          <Button
            onClick={() => setFilterType('payment')}
            variant={filterType === 'payment' ? 'default' : 'outline'}
            size="sm"
          >
            Payments
          </Button>
          <Button
            onClick={() => setFilterType('agreement')}
            variant={filterType === 'agreement' ? 'default' : 'outline'}
            size="sm"
          >
            Agreements
          </Button>
          <Button
            onClick={() => setFilterType('police_verification')}
            variant={filterType === 'police_verification' ? 'default' : 'outline'}
            size="sm"
          >
            Verifications
          </Button>
          <Button
            onClick={() => setFilterType('reminder')}
            variant={filterType === 'reminder' ? 'default' : 'outline'}
            size="sm"
          >
            Reminders
          </Button>
          <Button
            onClick={() => setFilterType('expense')}
            variant={filterType === 'expense' ? 'default' : 'outline'}
            size="sm"
          >
            Expenses
          </Button>
        </div>
      </Card>

      {/* Timeline */}
      <Card className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Activity Timeline</h3>
        
        {filteredTimeline.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No activities found for this filter
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            
            {/* Timeline Events */}
            <div className="space-y-6">
              {filteredTimeline.map((event, index) => (
                <div key={event.id || index} className="relative flex gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getColorClass(event.color)} z-10`}>
                    {getIcon(event.icon)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{event.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                        </div>
                        <span className="text-xs text-gray-500">{formatDateTime(event.date)}</span>
                      </div>
                      
                      {/* Additional Data */}
                      {event.data && event.type === 'payment' && (
                        <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Amount:</span>
                            <span className="ml-2 font-medium">₹{event.data.amount}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Method:</span>
                            <span className="ml-2 font-medium">{event.data.payment_method}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Status:</span>
                            <span className={`ml-2 font-medium ${
                              event.data.status === 'completed' ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {event.data.status}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {event.data && event.type === 'agreement' && (
                        <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Duration:</span>
                            <span className="ml-2 font-medium">
                              {formatDate(event.data.start_date)} to {formatDate(event.data.end_date)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Deposit:</span>
                            <span className="ml-2 font-medium">₹{event.data.deposit}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Lifecycle modals */}
      <PutOnNoticeModal
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        tenant={tenant}
        token={token}
        onSaved={fetchTenantProfile}
      />
      <EditCheckoutDateModal
        open={editCheckoutOpen}
        onClose={() => setEditCheckoutOpen(false)}
        tenant={tenant}
        token={token}
        onSaved={fetchTenantProfile}
      />
      <MarkExitedModal
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        tenant={tenant}
        token={token}
        onSaved={fetchTenantProfile}
      />
      <CancelNoticeModal
        open={cancelNoticeOpen}
        onClose={() => setCancelNoticeOpen(false)}
        tenant={tenant}
        token={token}
        onSaved={fetchTenantProfile}
      />
      <RecordPaymentModal
        isOpen={recordPayOpen}
        onClose={() => setRecordPayOpen(false)}
        onSuccess={fetchTenantProfile}
        token={token}
        presetTenant={tenant}
      />
    </div>
  );
};

export default TenantProfile;
